/**
 * Pure(-ish) helper that drives a browser file download from a Blob.
 *
 * Side effects (`createObjectURL`, anchor click, `revokeObjectURL`) are routed
 * through a {@link DownloadTrigger} so tests can inject fakes instead of having
 * to stub `window.URL` and `document.createElement` globally. The default
 * trigger wires the real browser APIs; production call sites can omit it.
 */
export interface DownloadTrigger {
  createObjectURL: (blob: Blob) => string;
  revokeObjectURL: (url: string) => void;
  createAnchor: () => HTMLAnchorElement;
}

/**
 * Default trigger that uses the real `URL` + `document.createElement` APIs.
 * Exported for symmetry with the DI signature, but {@link triggerDownload}
 * already substitutes this when the caller passes no trigger.
 */
export const defaultDownloadTrigger: DownloadTrigger = {
  createObjectURL: (blob) => URL.createObjectURL(blob),
  revokeObjectURL: (url) => URL.revokeObjectURL(url),
  createAnchor: () => document.createElement("a"),
};

/**
 * Triggers a file download by attaching an offscreen anchor to the document,
 * clicking it, then cleaning up. The created object URL is revoked in a
 * `finally` block so we never leak a URL even if the anchor click throws (e.g.
 * jsdom's stubbed click on some browser versions).
 *
 * Slice 5 / Phase 6: this is the one and only path the export modal uses to
 * land a Blob on disk. See `use-export.ts::useDownloadExport` for the caller.
 */
export function triggerDownload(
  blob: Blob,
  filename: string,
  trigger: DownloadTrigger = defaultDownloadTrigger,
): void {
  const url = trigger.createObjectURL(blob);
  try {
    const anchor = trigger.createAnchor();
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } finally {
      document.body.removeChild(anchor);
    }
  } finally {
    trigger.revokeObjectURL(url);
  }
}
